// src/com/auto_cpp_info/AutoCppInfo.js
import React, { useEffect, useRef, useState } from 'react';
import { Box } from '@mui/system';
import { styled } from '@mui/material/styles';
import { IconButton, CircularProgress } from '@mui/material';
import CachedIcon from '@mui/icons-material/Cached'; // refresh
import NoteAddIcon from '@mui/icons-material/NoteAdd';
import EditIcon from '@mui/icons-material/Edit';
import DeleteForeverIcon from '@mui/icons-material/DeleteForever';
import HomeWorkIcon from '@mui/icons-material/HomeWork';
import CheckIcon from '@mui/icons-material/Check'; // selected dc
import RemoveIcon from '@mui/icons-material/Remove'; //no com list
import KeyboardArrowRightIcon from '@mui/icons-material/KeyboardArrowRight'; // closed com list
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown'; // opened com list
import { useLazyQuery, useMutation, useReactiveVar } from '@apollo/client';
import { useSnackbar } from 'notistack';
import SearchBox from '../../common/SearchBox';
import {ContentConainer, ContentHeader, ContentBody, ControlBox, ToolBox} from '../../common/TopViewControl';
import { useConfirmDialog } from '../../common/AppConfirmDialog';
import { AppObject, AppStyle, AppWord } from '../../value/AppConst';
import { authTableTreeRepo, autoCppInfoListRepo, autoCppInfoParamRepo, comInfoListRepo, userInfoRepo } from '../../value/AppModel';
import {
    LIST_AUTO_CPP_INFO, GET_AUTO_CPP_INFO,
    ADD_AUTO_CPP_INFO, EDIT_AUTO_CPP_INFO, REMOVE_AUTO_CPP_INFO
} from './AutoCppInfoGql';
import { LIST_COM_INFO, makeComInfoTree } from '../com_info/ComInfoGql';
import { AutoCppInfoDictionary } from './AutoCppInfoDict';
import { useAlertSetter, useAuthorizationCheck } from '../../api/app-hook';
import FlexyTable from '../../common/flexytable/FlexyTable';
import AutoCppInfoInput from './AutoCppInfoInput';
import { NonCapButton } from '../../common/VzmStyled';
import MonthPicker from '../../common/MonthPicker';
import ValueMisc from '../../value/ValueMisc';

const ColumnContainer = styled(Box)({
    display: 'flex', flexDirection: 'column', boxSizing: 'border-box',
});

const DcTypeLine = styled('div')({
    display: 'flex', alignItems: 'center', backgroundColor: '#eee'
});
const DcTypeLabel = styled('span')({
    fontSize: '0.9rem', color: AppStyle.PrimaryRGB, width: 100, display: 'inline-block',
    padding: 5, fontWeight:'bold' // border: AppStyle.BorderCCC, borderRadius: 5, textAlign: 'center',
});

const DcGroup = styled('div')({
    marginLeft: 32, fontSize: '0.85rem',
});
const DcLabelLine = styled('div')({
    display: 'flex', alignItems: 'center',
});

const ComListBox = styled('div')({
    marginLeft: 40
});

const ComName = styled('div')({padding:2, color:'#777', fontSize:'0.8rem'});

/**
 * (AUTO_CPP_INFO) 정보 View Controller
 * 출고 예정수량 등록 or DC별 일일 물동량 볼륨
 * Using React with MUI5
 * Project: NewTOP for cloud
 * 2024-11-01 17:22:54 using DbdbTap
 * 
 * 기존 화면과 달리 한꺼번에 모든 센터의 데이터를 가지고 온 후 센터 트리에서
 * 선택하면 해당 센터의 것들만 테이블에 보여주는 방식으로 변경.
 * 
 * 기존 화면에는 TPC 타겟 저장이 있었음(아이콘)
    PR_AUTO_TPC_TARGET_INFO_EDIT 프로시저를 호출하여 TPC 타겟을 저장하는 것인데,
    실제 데이터베이스에서 AUTO_PLAN_TPC_TARGET_INFO 테이블을 보면
    2019.2월이 마지막이고 총 5개 레코드 밖에 없음
    즉, 사용하지 않고 있음.
    클라우드 이전 프로젝트에서는 제외함.
 */
export default function AutoCppInfo({
    viewServiceTags // for switching view
}) {
    const sessionInfo = useReactiveVar(userInfoRepo);
    const authTableTree = useReactiveVar(authTableTreeRepo);
    const [dcToOpen, setDcToOpen] = useState(null); // dc_cd to see com_info list in the tree.
    const [editorState, setEditorState] = useState({...AppObject.EmptyViewerState, item:{}});
    // const [monthSelected, setMonthSelected] = useState(null);
    const autoCppInfoListData = useReactiveVar(autoCppInfoListRepo);
    const [autoCppListForDc, setAutoCppListForDc] = useState([]); // 선택한 DC의 데이터만 보여주기 위한 변수
    const autoCppInfoParam = useReactiveVar(autoCppInfoParamRepo); // {dcCd, planDate} 화면 전환에도 정보 유지되도록 model로 관리
    const comInfoListData = useReactiveVar(comInfoListRepo);
    const [comInfoTree, setComInfoTree] = useState([]); // from comInfoListData. dcTypeCd => dcCd => comId
    const [searchText, setSearchText] = useState('');
    const openConfirmDialog = useConfirmDialog();
    const checkAuth = useAuthorizationCheck();
    const addAlert = useAlertSetter();
    const { enqueueSnackbar } = useSnackbar();
    const lastReloadRef = useRef(0);
    const isEditingRef = useRef(false); // 플래그 생성

    // ##### Call GraphQL to get List #####
    const [getComInfoList, responseComList] = useLazyQuery(LIST_COM_INFO, {
        ...AppObject.NoCachedFetch,
        onCompleted: (data, option) => {onCompleteGetComList(data, option)},
        onError: (error) => addAlert({title: '운송사 목록 조회 오류', message: [error.message]})
    });

    const [getAutoCppInfoList, responseList] = useLazyQuery(LIST_AUTO_CPP_INFO, {
        ...AppObject.NoCachedFetch,
        onCompleted: (data, option) => {onCompleteGetList(data, option)},
		onError: (error) => addAlert({title: AutoCppInfoDictionary.errorTitle.Get, message: [error.message]})
    });
    const [getAutoCppInfoItem, responseItem] = useLazyQuery(GET_AUTO_CPP_INFO, {
		...AppObject.NoCachedFetch,
		onCompleted: (data, option) => {onCompleteGetItem(data, option)},
		onError: (error) => addAlert({title: AutoCppInfoDictionary.errorTitle.Get, message: [error.message]})
	});
    
    // ##### GraphQL Mutation. 'onError' for prevent from printing error at console (Uncaught exception) ###
    const [addAutoCppInfo, responseAdd] = useMutation( ADD_AUTO_CPP_INFO, {
		onCompleted: (data, option) => onCompleteAdd(data, option), 
		onError: (error) => addAlert({title: AutoCppInfoDictionary.errorTitle.Add, message: [error.message]})
	} );
    const [editAutoCppInfo, responseEdit] = useMutation( EDIT_AUTO_CPP_INFO, {
		onCompleted: (data, option) => onCompleteEdit(data, option),
		onError: (error) => addAlert({title: AutoCppInfoDictionary.errorTitle.Edit, message: [error.message]})
	} );
    const [removeAutoCppInfo, responseRemove] = useMutation( REMOVE_AUTO_CPP_INFO, {
		onCompleted: (data, option) => onCompleteRemove(data, option),
		onError: (error) => addAlert({title: AutoCppInfoDictionary.errorTitle.Remove, message: [error.message]})
	});

    const getAutoCppInfoListNow = (ymd) => {
        const param = {variables:{
            planDate: ymd ? ymd : autoCppInfoParam.planDate || ValueMisc.getYMD(ValueMisc.getFirtDateOfMonth())
        }};
        getAutoCppInfoList(param);
        lastReloadRef.current = new Date().getTime()/1000;
    };

    // Set default DC if not set.
    useEffect(()=>{
        if(!autoCppInfoParam.dcCd) {
            autoCppInfoParamRepo({dcCd:sessionInfo.user.getDcCd(), planDate:autoCppInfoParam.planDate});
        }
    }, [autoCppInfoParam, sessionInfo]);

    // 로드되면 목록들을 가져온다.
    useEffect(() => {
        // DC와 관계 없이 모든 한달 데이터를 가져온다. 가져온 후 DC로 필터링.
        const ymd = autoCppInfoParam.planDate || ValueMisc.getYMD(ValueMisc.getFirtDateOfMonth());
        let diff = new Date().getTime()/1000 - autoCppInfoListData.when;
        if(diff > 300 || ymd !== autoCppInfoParam.planDate) {
            getAutoCppInfoListNow(ymd);
        }
        diff = new Date().getTime()/1000 - comInfoListData.when;
        if(diff > 300) {
            getComInfoList();
        }
    }, []);

    // 업체 목록이 로드되면 트리로 만든다.
    useEffect(()=>{
        if(comInfoListData.records.length > 0) {
            const tree = makeComInfoTree(comInfoListData.records);
            setComInfoTree(tree);
        }
    }, [comInfoListData]);

    // DC별 데이터만 보여주기 위해 필터링
    useEffect(()=>{
        const dcCd = autoCppInfoParam.dcCd || sessionInfo.user.getDcCd();
        setAutoCppListForDc(
            autoCppInfoListData.records.filter((item)=>item.dcCd === dcCd)
            .sort((a,b)=>b.planDate.localeCompare(a.planDate))
        );
    }, [autoCppInfoListData, autoCppInfoParam, sessionInfo]);

    // >>>>>>>>> callbacks <<<<<<<<<<<<<
    const onCompleteGetComList = (data, option) => {
        if(data.comInfoList) {
            comInfoListRepo({when:new Date().getTime()/1000, records:data.comInfoList});
        }
    };

    const onCompleteGetList = (data, clientOption) => {
        if(data.autoCppInfoList) {
            autoCppInfoListRepo({when:new Date().getTime()/1000, records:data.autoCppInfoList});
        }
    };

    const onCompleteGetItem = (data, option) => {
        if(data.autoCppInfoItem) {
            const newItem = {...data.autoCppInfoItem};
            newItem.isEdit = true;
            setEditorState({open:true, item:newItem});
        }
        else addAlert({title:AutoCppInfoDictionary.errorTitle.Get, message:['지정한 데이터를 찾을 수 없습니다.']});
    };

    const onCompleteAdd = (data, clientOption) => {
        if(data.autoCppInfoAdd.ok) {
            setEditorState({open:false, item:{}});
            getAutoCppInfoListNow();
        }
        else addAlert({title:AutoCppInfoDictionary.errorTitle.Add, message:[data.autoCppInfoAdd.message]});
    };

    const onCompleteEdit = (data, clientOption) => {
        if(data.autoCppInfoEdit.ok) {
            setEditorState({open:false, item:{}});
            getAutoCppInfoListNow();
        }
        else addAlert({title:AutoCppInfoDictionary.errorTitle.Edit, message:[data.autoCppInfoEdit.message]});
    };

    const onCompleteRemove = (data, clientOption) => {
        if(data.autoCppInfoRemove.ok) getAutoCppInfoListNow();
        else addAlert({title:AutoCppInfoDictionary.errorTitle.Remove, message:[data.autoCppInfoRemove.message]});
    };

    // >>>>>>>>> Event Handler <<<<<<<<<<<<<

    // 월 선택시
    const onChangeMonth = (id, value) => {
        autoCppInfoParamRepo({planDate: value, dcCd: autoCppInfoParam.dcCd});
        // get list here.
        getAutoCppInfoListNow(value);
    };

    const onClickDc = (dc) => {
        autoCppInfoParamRepo({dcCd:dc.dcCd, planDate:autoCppInfoParam.planDate});
    };

    const onClickReload = () => {
        const diff = new Date().getTime()/1000 - lastReloadRef.current;
        if(diff < 60) {
            // Too short to reload
            enqueueSnackbar(AppWord.ReloadNotInOneMinute, {variant:'info', autoHideDuration:2000});
        }
        else getAutoCppInfoListNow();
    };

    const onRequestSave = (item, isEdit) => {
        const mutateParam = {variables:{autoCppInfo:item}};
        if(isEdit) editAutoCppInfo(mutateParam);
        else addAutoCppInfo(mutateParam);
    };

    const onClickAdd = () => {
        const dcCd = autoCppInfoParam.dcCd || sessionInfo.user.getDcCd();
        const dc = comInfoListData.records.find((com)=>com.dcCd === dcCd);
        const planDate = ValueMisc.getYMD(new Date());
        if(dc) {
            setEditorState({
                open:true,
                item:{
                    dcCd:dcCd, 
                    planDate,
                    dcName:dc.dcName,
                }});
        }
        else {
            addAlert({
                title: '물류센터 정보 오류',
                message: ['물류센터 정보를 찾을 수 없습니다.','관리자에게 문의하세요.']
            })
        }
    };

    const onRequestEdit = (item) => {
        isEditingRef.current = true;
        getAutoCppInfoItem({variables:{dcCd:item.dcCd, planDate:item.planDate}});
    };

    const confirmRemove = async (item) => {
        const confirm = await openConfirmDialog({
            title: "출고예정수량 정보 삭제",
            content: [
                `출고예정수량 정보(${item.planDate})를 삭제하시겠습니까?`,
                '삭제 후 복구할 수 없습니다. 계속하시겠습니까?'
            ]
        });

        if(confirm)
            removeAutoCppInfo({variables:{autoCppInfo:{dcCd:item.dcCd, planDate:item.planDate}}});
    };

    const onChangeSearchText = (text) => {
        setSearchText(text);
    };


    const onClickToolOnRow = (toolId, row) => {
        switch(toolId) {
            case AppObject.command.DELETE: confirmRemove(row); break;
            case AppObject.command.EDIT: onRequestEdit(row); break;
            default: return;
        }
    };

    /*
	CALL wps_set_auth_table('pr_auto_cpp_info_add', '1,2', v_msg);
	CALL wps_set_auth_table('pr_auto_cpp_info_edit', '1,2', v_msg);
	CALL wps_set_auth_table('pr_auto_cpp_info_remove', '1,2', v_msg);
    */

    const canAdd = checkAuth(authTableTree.tree.pr_auto_cpp_info_add);
    const canEdit = checkAuth(authTableTree.tree.pr_auto_cpp_info_edit);
    const canRemove = checkAuth(authTableTree.tree.pr_auto_cpp_info_remove);
    /* const tools = canEdit ? [
        {id:AppObject.command.EDIT, icon:<EditIcon/>},
        {id:AppObject.command.DELETE, icon:<DeleteForeverIcon/>, color: AppStyle.mui.warning},
    ] : []; */
    const tools = [];
    if(canEdit) tools.push({id:AppObject.command.EDIT, icon:<EditIcon/>});
    if(canRemove) tools.push({id:AppObject.command.DELETE, icon:<DeleteForeverIcon/>, color: AppStyle.mui.warning});

    const loadingAny
    = responseList.loading
    || responseComList.loading
    || responseItem.loading
    || responseAdd.loading
    || responseEdit.loading
    || responseRemove.loading;

    const renderComListBox = (dc) => {
        return(
            <ComListBox>
                {
                    dc.comList
                    .sort((a,b)=>a.comName.localeCompare(b.comName))
                    .map((com) => {
                        return <ComName key={com.comId}>- {com.comName}</ComName>
                    })
                }
            </ComListBox>
        );
    }

    const renderComTree = () => {
        return(
            <Box
                style={{
                    position: 'absolute', top: 0, left: 0, right: 0, bottom: 0,
                    overflow: 'auto', 
                }}
            >
                {
                    comInfoTree
                    .map((dcType, idx) => {
                        return (
                            <Box key={dcType.dcTypeCd}>
                                <DcTypeLine>
                                    <DcTypeLabel>{dcType.dcTypeName}</DcTypeLabel>
                                </DcTypeLine>
                                <div>
                                    {
                                        dcType.dcList
                                        .sort((a,b)=>a.dcName.localeCompare(b.dcName))
                                        .map((dc) => {
                                            return (
                                                <DcGroup key={dc.dcCd}>
                                                    <DcLabelLine>
                                                        {
                                                            dc.comList.length === 0 ?
                                                            <IconButton
                                                                disabled
                                                                size={AppWord.SMALL}
                                                            >
                                                                <RemoveIcon style={{fontSize:12, color:'#777'}} />
                                                            </IconButton>
                                                            :
                                                            dcToOpen === dc.dcCd ?
                                                            <IconButton
                                                                size={AppWord.SMALL}
                                                                onClick={()=>setDcToOpen(null)}
                                                            >
                                                                <KeyboardArrowDownIcon style={{fontSize:12, color:'#777'}} />
                                                            </IconButton>
                                                            :
                                                            <IconButton
                                                                size={AppWord.SMALL}
                                                                onClick={()=>setDcToOpen(dc.dcCd)}
                                                            >
                                                                <KeyboardArrowRightIcon style={{fontSize:12, color:'#777'}} />
                                                            </IconButton>
                                                        }
                                                        <NonCapButton
                                                            size={AppWord.SMALL}
                                                            startIcon={
                                                                autoCppInfoParam.dcCd === dc.dcCd ?
                                                                <CheckIcon style={{fontSize:12, color:AppStyle.PrimaryRGB}} />
                                                                : <HomeWorkIcon style={{fontSize:12, color:'#777'}} />
                                                            }
                                                            onClick={()=>onClickDc(dc)}
                                                            disabled={autoCppInfoParam.dcCd === dc.dcCd || responseList.loading}
                                                        >
                                                            {dc.dcName}
                                                        </NonCapButton>
                                                    </DcLabelLine>
                                                    {dc.dcCd === dcToOpen && renderComListBox(dc)}
                                                </DcGroup>
                                            )
                                        })
                                    }
                                </div>
                            </Box>
                        )
                    })
                }
            </Box>
        );
    };

    return(
        <ContentConainer>
            {viewServiceTags}
            <ContentBody
                style={{display:'flex'}}
            >
                <ColumnContainer width={240} style={{paddingRight:10}}>
                    <ContentHeader>
                        <ControlBox>
                            <MonthPicker
                                dict={{id:'dateFrom', label:'월 선택'}}
                                value={autoCppInfoParam.planDate}
                                monthsAgo={30}
                                upToAfter={6}
                                onChange={onChangeMonth}
                            />
                        </ControlBox>
                    </ContentHeader>
                    <Box style={{
                            flexGrow:1, position:'relative',
                            border: AppStyle.BorderCCC, boxSizing:'border-box',
                            borderRadius:5, padding:10,
                    }}>
                        {renderComTree()}
                    </Box>
                </ColumnContainer>
                <ColumnContainer flexGrow={1}>
                    <ContentHeader>
                        <ControlBox>
                            <SearchBox
                                value={searchText}
                                onChangeText={onChangeSearchText}
                            />
                        </ControlBox>
                        <ToolBox>
                            {
                                loadingAny ?
                                <CircularProgress size={20} />
                                :
                                <IconButton
                                    color={AppStyle.mui.inherit}
                                    onClick={onClickReload}
                                >
                                    <CachedIcon />
                                </IconButton>
                            }
                            {
                                canAdd &&
                                <IconButton
                                    color={AppStyle.mui.primary}
                                    style={{marginLeft:10}}
                                    onClick={onClickAdd}
                                    disabled={
                                        loadingAny
                                    }
                                >
                                    <NoteAddIcon />
                                </IconButton>
                            }
                        </ToolBox>
                    </ContentHeader>
                    <FlexyTable
                        name={AutoCppInfoDictionary.tableProps.tableId}
                        tableProps={AutoCppInfoDictionary.tableProps}
                        records={autoCppListForDc}
                        search={searchText}
                        tools={tools}
                        onClickTool={onClickToolOnRow}
                    />
                </ColumnContainer>
            </ContentBody>
            <AutoCppInfoInput
                open={editorState.open}
                item={editorState.item}
                isEdit={editorState.item.isEdit}
                onClose={()=>setEditorState({open:false, item:{}})}
                responseSaving={editorState.item ? responseEdit : responseAdd}
                onRequestSave={onRequestSave}
            />
        </ContentConainer>
    );
}
